import { useMemo } from 'react';
import { useQueries } from 'react-query';
import { CallGroupApi } from '@frontend/api-call-group';
import { CallPathV1 } from '@frontend/api-call-path';
import {
  useAnalyticsOrgLocations,
  formatEndDateWithTimeZone,
  formatStartDateWithTimeZone,
} from '@frontend/analytics-shared';
import { usePhoneAnalyticsShallowStore } from '../../hooks/use-phone-analytics-store';
import { PhoneAnalyticsEntity } from './types';
import { GroupPanelContainer } from '.';

export const CallGroupPanel = () => {
  const { filters, isDemoAccount, filterHintText } = usePhoneAnalyticsShallowStore(
    'filters',
    'isDemoAccount',
    'filterHintText'
  );

  const { accessibleLocationMap } = useAnalyticsOrgLocations({
    isAllAccessScopeEnabled: true,
    isDemoAccount,
    module: 'PHONE',
  });

  const tenantIds = useMemo(
    () =>
      Array.from(
        new Set(
          filters.location_id
            ?.map((loc) => accessibleLocationMap?.[loc]?.phoneTenantId)
            .filter((id): id is string => !!id)
        )
      ),
    [filters.location_id, accessibleLocationMap]
  );

  const queries = useQueries(
    tenantIds.map((tenantId) => ({
      queryKey: [filters.location_id, 'phone', 'listCallGroups', tenantId],
      queryFn: () => CallGroupApi.listCallGroups({ tenantId }),
      staleTime: 0,
      retry: false,
    }))
  );

  const queriesData = useMemo(
    () =>
      queries.flatMap((q, idx) =>
        (q.data?.callGroups ?? [])
          .filter(({ name }) => !name.toLowerCase().includes('e911'))
          .map((callGroup) => ({
            tenantId: tenantIds[idx],
            id: callGroup.id,
            name: callGroup.name,
            devicesCount: callGroup.devicesCount,
          }))
      ),
    [queries, tenantIds]
  );

  const tenantIdCallGroupIdsMap = useMemo(
    () =>
      queriesData.reduce((acc, cg) => {
        if (!acc[cg.tenantId]) {
          acc[cg.tenantId] = { callGroupIds: [] };
        }
        acc[cg.tenantId].callGroupIds.push(cg.id);
        return acc;
      }, {} as Record<string, { callGroupIds: string[] }>),
    [queriesData]
  );

  const { startTime, endTime } = useMemo(
    () => ({
      startTime: formatStartDateWithTimeZone(filters.start_date),
      endTime: formatEndDateWithTimeZone(filters.end_date),
    }),
    [filters.start_date, filters.end_date]
  );

  const { data: callGroupAnalyticsData, isLoading } =
    CallPathV1.CallPathQueries.useFetchCallGroupAnalyticsForMultiTenantQuery(
      {
        tenantIdCallGroupIdsMap,
        startTime,
        endTime,
      },
      {
        retry: 3,
        enabled: Object.keys(tenantIdCallGroupIdsMap).length > 0,
      }
    );

  const customNameHeaders = useMemo(
    () =>
      queriesData.reduce((acc, cg) => {
        const found = callGroupAnalyticsData?.data?.find((d) => d.callGroupId === cg.id);
        if (found) {
          acc[cg.id] = {
            name: cg.name,
            devicesCount: cg.devicesCount,
          };
        }
        return acc;
      }, {} as Record<string, { name: string; devicesCount: number }>),
    [callGroupAnalyticsData?.data, queriesData]
  );

  return (
    <GroupPanelContainer
      data={callGroupAnalyticsData?.data}
      isLoading={isLoading}
      customNameHeaders={customNameHeaders}
      filterHintText={filterHintText}
      entityType={PhoneAnalyticsEntity.Group}
    />
  );
};
